// Default Import
import React, { useEffect, useState } from 'react';
import { Card, Stack } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Image from 'react-bootstrap/Image';
import Row from 'react-bootstrap/Row';
import { Link, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

// Common Component
import CommonFormikComponent from '../../components/common/CommonFormikComponent';
import InputText from '../../components/common/InputText';
import Loader from '../../components/common/Loader';

// Helpers
import storage from '../../helpers/storage';

// Icons
import { MdKeyboardBackspace } from 'react-icons/md';

// Images
import AuthBanner from "../../assets/images/forgot-password-banner.jpg";
import Logo from '../../assets/images/logo.svg';

// Services
import { forgotPassword } from '../../services/auth.service';

// Validations
import { ForgotSchema } from './validation';

/**
 * Form values type definition.
 */
type FormValues = {
    email: string;
}

export default function ForgotPassword() {
    const AppName = process.env.REACT_APP_NAME;

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [captchaCompleted, setCaptchaCompleted] = useState<boolean>(false);
    const reCapthaSiteKey: any = process.env.REACT_APP_RECAPTCHA_SITE_KEY


    /**
     * Initial Form Values
     */
    const initialValues: FormValues = {
        email: "",
    };

    useEffect(() => {
        if (storage.getToken() != null) {
            return navigate('/login')
        }
    }, [navigate])


    /**
     * Submit Method to call when user clicks on login button
     * @async
     * @param {FormValues} values
     * @param {any} actions
     * @returns {*}
     */
    const handleSubmit = (values: Record<string, any>, actions: any) => {
        setLoading(true);
        actions.setSubmitting(true);
        const formData: any = {
            email: values.email
        };

        forgotPassword(formData).then((response: any) => {
            setLoading(false);
            actions.setSubmitting(false);
            navigate('/login');
        }).catch((error: any) => {
            setLoading(false);
            actions.setSubmitting(false);
        })
    }

    return (
        <React.Fragment>
            <Loader isLoading={loading} />
            <Row className="g-0 vh-100 position-relative z-1">
                <Col md className="h-100 overflow-auto">
                    <Row className="justify-content-center g-0 h-100">
                        <Col lg={12} className="p-3">
                            <div className="custom-max-width-320 m-auto auth-padding-inner">
                                <div className='mb-4 pb-3'>
                                    <Link to='/login' className='align-items-center d-inline-flex link-dark link-opacity-100 link-opacity-75-hover text-decoration-none'>
                                        <MdKeyboardBackspace className="me-2" size={20} />
                                        <span className='fs-15'>Back to Login</span>
                                    </Link>
                                </div>
                                <div className="mb-5">
                                    <Link to="/" className="d-inline-block">
                                        <Image
                                            className="img-fluid"
                                            src={Logo}
                                            alt={`${AppName} Logo`}
                                            width={174}
                                            height={60}
                                        />
                                    </Link>
                                </div>
                                <h1 className="fw-semibold fs-2 mb-0">Forgot Password</h1>
                                <p className="mb-4 pb-1 opacity-75">Please enter your registered email address.</p>

                                <CommonFormikComponent
                                    validationSchema={ForgotSchema}
                                    initialValues={initialValues}
                                    onSubmit={handleSubmit}
                                >
                                    {(formikProps) => (
                                        <>
                                            <InputText
                                                wrapperClassname="mb-3 pb-1"
                                                controlId="email"
                                                label='Email Address*'
                                                placeholder="john.smith@example.com"
                                                touched={formikProps.touched.email}
                                                handleBlur={formikProps.handleBlur}
                                                handleChange={formikProps.handleChange}
                                                errorsField={formikProps.errors.email}
                                                value={formikProps.values.email}
                                            />

                                            <Form.Group className="mb-3 pb-1">
                                                <ReCAPTCHA
                                                    sitekey={reCapthaSiteKey}
                                                    onChange={() => setCaptchaCompleted(true)}
                                                />
                                            </Form.Group>
                                            <Stack direction="horizontal" gap={3} className="justify-content-end">
                                                <Button className="custom-min-width-85" variant="dark" type="submit" disabled={formikProps.isSubmitting || !captchaCompleted || loading}>
                                                    Submit
                                                </Button>
                                            </Stack>
                                        </>
                                    )}
                                </CommonFormikComponent>
                            </div>
                        </Col>
                    </Row>
                </Col>
                <Col
                    md={6}
                    lg={7}
                    xl={8}
                    className="h-100 start-0 top-0 z-n1 order-md-first d-none d-md-block"
                >
                    <Card className="h-100 text-white border-0">
                        <Image
                            className="h-100 object-fit-cover w-100"
                            src={AuthBanner}
                            alt="Forgot Banner"
                        />
                    </Card>

                </Col>
            </Row>
        </React.Fragment>
    )
}
